import http from "../utils/http"

export interface EnergyEstimateBreakdown {
  id: number
  category: string
  totalElectricityKwh: string
  totalGasKwh: string
}

export interface EnergyEstimate {
  id: number
  home: number
  totalElectricityKwh: string
  totalGasKwh: string
  totalHouselholdGasKwh?: string
  scenario: string
  breakdowns?: EnergyEstimateBreakdown[]
}

export interface EstimatedEpcAfterUpgrades {
  currentScore: number
  currentBand?: string | null
  estimatedScore: number
  estimatedBand?: string | null
  scoreRange: {
    low: number
    high: number
  }
  bandRange: {
    low?: string | null
    high?: string | null
  }
  scoreUplift: {
    low: number
    mid: number
    high: number
  }
  confidence: "high" | "medium" | "low"
  method: string
  inputs: {
    solarPanels?: number | null
    solarKw?: number | null
    solarGenerationKwh?: number | null
    batteryKwh?: number | null
    heatPumpKw?: number | null
    spaceHeatingDemandKwh?: number | null
    heatPumpElectricityKwh?: number | null
    nonHeatingElectricityKwh?: number | null
    householdGasKwh?: number | null
    postUpgradeElectricityDemandKwh?: number | null
    netGridImportKwh?: number | null
    solarCoverageRatio?: number | null
    floorArea?: number | null
    baselineDeliveredEnergyKwh?: number | null
    currentHeating?: string | null
    currentEnergyCost?: number | null
    postUpgradeEnergyCost?: number | null
    annualSavings?: number | null
  }
  officialPotential?: {
    score?: number | null
    band?: string | null
  }
}

export interface Home {
  id: number
  uprn: string
  fullAddress: string
  addressLine1: string
  addressLine2: string
  postcode: string
  city: string
  country: string
  lat: string
  lng: string
  osCoordinatesLat?: string | null
  osCoordinatesLng?: string | null
  lmkKey: string
  portfolio: number
  status: 'missing_data' | 'failed' | 'analysed'
  imageUrl?: string
  satelliteImageUrl?: string
  createdAt: string
  updatedAt: string
  netGridImportKwh?: number | null
  zeroBillPotential?: string | null
  upgradesRoi?: number | null
  upgradesTotalCost?: number | null
  upgradesAnnualSavings?: number | null
  upgradesPaybackPeriod?: number | null
  tenantPowerRoi?: number | null
  tenantPowerPaybackPeriod?: number | null
  kanbanStage?: string | null
  estimatedEpcAfterUpgrades?: EstimatedEpcAfterUpgrades | null
  features: {
    storeys: number
    buildingGeometry: number
    roofAreaTotal: number
    roofTotalFlatSqm: number
    roofTotalPitchedSqm: number
    roofHeightRelativeMaxM: number
    roofHeightRelativeRoofBaseM: number
    roofShapeAspectAreaFacingEastM2: number
    roofShapeAspectAreaFacingWestM2: number
    roofShapeAspectAreaFacingSouthM2: number
    roofShapeAspectAreaFacingNorthM2: number
    roofShapeAspectAreaIndeterminableM2: number
    wallType: string
    wallInsulation: string
    wallInsulationAssumed: number
    wallCavityFilled: number
    wallExternalInsulation: number
    wallInternalInsulation: number
    roofType: string
    roofInsulated: string
    roofInsulationAssumed: number
    roofInsulationThickness?: number
    floorType: string
    floorInsulated: string
    floorInsulationAssumed: number
    constructionYear: number
    epcInspectionDate: string
    floorArea: number
    windowsType: string
    primaryHeating: string
    hotWater: string
    lightingEfficiency: string
    constructionAgeBand: string
    extensionCount: number
    numberOfHabitableRooms: number
    numberOfHeatedRooms: number
    mainFuel: string
    ventilationType: string
    builtForm: string
    propertyType: string
    potentialEpcEfficiency?: number
    potentialRating?: string
    currentEpcEfficiency?: number
    currentEpcRating?: string
  }
}

export interface PortfolioHomesResponse {
  portfolioId: number
  portfolioName: string
  homes: Home[]
  totalHomes: number
  nextCursor: string | null
  hasMore: boolean
}

export const addHome = async (payload: any) => {
  return http.post("/api/portfolio/add-home/", payload)
}

export const getHomes = async (
  portfolioId?: number
): Promise<PortfolioHomesResponse> => {
  const params = portfolioId ? { portfolio_id: portfolioId } : {}
  const response = await http.get("/api/portfolio/homes/", { params })
  if (response.data && Array.isArray(response.data.homes)) {
    return response.data
  } else {
    console.warn("Unexpected response structure:", response.data)
    return {
      portfolioId: 0,
      portfolioName: "",
      homes: [],
      totalHomes: 0,
      nextCursor: null,
      hasMore: false
    } as PortfolioHomesResponse
  }
}

export interface GetHomesPaginatedParams {
  cursor?: string
  limit?: number
  portfolioId?: number
  search?: string
  builtForm?: string
  sort?: 'recent' | 'oldest' | 'alphabetical' | 'highest_roi'
  roiPotential?: string
  minEpcBand?: string
  maxEpcBand?: string
  /** Bands to include: "A"-"G" plus "none" for unrated homes. Takes precedence over min/max. */
  epcBands?: string[]
}

export interface ExportHomesAnalysisCsvParams {
  portfolioId?: number
  search?: string
  builtForm?: string
  sort?: 'recent' | 'oldest' | 'alphabetical' | 'highest_roi'
  roiPotential?: string
}

export const getHomesPaginated = async (
  params: GetHomesPaginatedParams = {}
): Promise<PortfolioHomesResponse> => {
  const searchParams = new URLSearchParams()
  if (params.cursor) searchParams.set("cursor", params.cursor)
  if (params.limit) searchParams.set("limit", params.limit.toString())
  if (params.portfolioId) searchParams.set("portfolio_id", params.portfolioId.toString())
  if (params.search) searchParams.set("search", params.search)
  if (params.builtForm) searchParams.set("built_form", params.builtForm)
  if (params.sort) searchParams.set("sort", params.sort)
  if (params.roiPotential) searchParams.set("roi_potential", params.roiPotential)
  if (params.minEpcBand) searchParams.set("min_epc_band", params.minEpcBand)
  if (params.maxEpcBand) searchParams.set("max_epc_band", params.maxEpcBand)
  if (params.epcBands?.length) searchParams.set("epc_bands", params.epcBands.join(","))

  const queryString = searchParams.toString()
  const url = `/api/portfolio/homes/${queryString ? `?${queryString}` : ""}`

  const response = await http.get(url)
  return response.data
}

export const exportHomesAnalysisCsv = async (
  params: ExportHomesAnalysisCsvParams = {}
) => {
  const searchParams = new URLSearchParams()
  if (params.portfolioId) searchParams.set("portfolio_id", params.portfolioId.toString())
  if (params.search) searchParams.set("search", params.search)
  if (params.builtForm) searchParams.set("built_form", params.builtForm)
  if (params.sort) searchParams.set("sort", params.sort)
  if (params.roiPotential) searchParams.set("roi_potential", params.roiPotential)

  const queryString = searchParams.toString()
  const url = `/api/portfolio/homes/export/${queryString ? `?${queryString}` : ""}`

  return http.get(url, { responseType: "blob" })
}

export interface PortfolioInfo {
  id: number
  name: string
  homeCount: number
  createdAt: string
}

export const listPortfolios = async (): Promise<PortfolioInfo[]> => {
  const response = await http.get("/api/portfolio/portfolios/")
  return response.data
}

export const createPortfolio = async (name: string): Promise<PortfolioInfo> => {
  const response = await http.post("/api/portfolio/portfolios/", { name })
  return response.data
}

export const renamePortfolio = async (id: number, name: string): Promise<PortfolioInfo> => {
  const response = await http.patch(`/api/portfolio/portfolios/${id}/`, { name })
  return response.data
}

export const deletePortfolio = async (id: number): Promise<void> => {
  await http.delete(`/api/portfolio/portfolios/${id}/delete/`)
}

export const deleteHome = async (homeId: number) => {
  return http.delete(`/api/portfolio/homes/${homeId}/delete/`)
}

export const markHomeInterested = async (homeId: number): Promise<{ kanbanStage: string }> => {
  const response = await http.patch(`/api/portfolio/homes/${homeId}/mark-interested/`)
  return response.data
}

export const moveHomeToPortfolio = async (homeId: number, targetPortfolioId: number, opportunityId?: number): Promise<{ homeId: number; portfolioId: number; message: string }> => {
  const body: Record<string, number> = { target_portfolio_id: targetPortfolioId }
  if (opportunityId) body.opportunity_id = opportunityId
  const response = await http.post(`/api/portfolio/homes/${homeId}/move/`, body)
  return response.data
}

export interface HeatLossCalculation {
  id: number
  totalHeatLoss: number
  wallHeatLoss: number
  roofHeatLoss: number
  floorHeatLoss: number
  windowHeatLoss: number
  doorHeatLoss: number
  ventilationHeatLoss: number
  calculationSuccessful: boolean
  errorMessage?: string
  totalFloorArea: number
  propertyType: string
  builtForm: string
  constructionAgeBand: string
  wallsDescription: string
  roofDescription: string
  floorDescription: string
  windowsDescription: string
  wallUvalue: number
  roofUvalue: number
  floorUvalue: number
  windowUvalue: number
  doorUvalue: number
  externalWallSurface: number
  windowsSurface: number
  doorsSurface: number
  roofSurface: number
  storeys: number
  ceilingHeight: number
  airVolume: number
  airChangeRate: number
  createdAt: string
  updatedAt: string
}

export interface SolarAnalysis {
  id: number
  home?: number
  maximumPanelsPossible: number
  maximumOutputPossibleKwh: string
  recommendedPanelCount: number
  recommendedPanelsOutputKwh: string
  panelWattage: number
  panelEfficiencyPercentage: string
  panelAreaSqm: string
  googleSolarRoofSurfaceSqm: string
  googleSolarAnnualSunshineHours: string
  googleSolarEstimatedKwh: string
  recommendedSouthPanels: number
  recommendedSouthOutputKwh: string
  recommendedSoutheastPanels: number
  recommendedSoutheastOutputKwh: string
  recommendedSouthwestPanels: number
  recommendedSouthwestOutputKwh: string
  recommendedEastPanels: number
  recommendedEastOutputKwh: string
  recommendedWestPanels: number
  recommendedWestOutputKwh: string
  recommendedNorthPanels: number
  recommendedNorthOutputKwh: string
  recommendedNortheastPanels: number
  recommendedNortheastOutputKwh: string
  recommendedNorthwestPanels: number
  recommendedNorthwestOutputKwh: string
  recommendedFlatPanels: number
  recommendedFlatOutputKwh: string
  maximumSouthPanels?: number
  maximumSouthOutputKwh?: string
  maximumSoutheastPanels?: number
  maximumSoutheastOutputKwh?: string
  maximumSouthwestPanels?: number
  maximumSouthwestOutputKwh?: string
  maximumEastPanels?: number
  maximumEastOutputKwh?: string
  maximumWestPanels?: number
  maximumWestOutputKwh?: string
  maximumNorthPanels?: number
  maximumNorthOutputKwh?: string
  maximumNortheastPanels?: number
  maximumNortheastOutputKwh?: string
  maximumNorthwestPanels?: number
  maximumNorthwestOutputKwh?: string
  maximumFlatPanels?: number
  maximumFlatOutputKwh?: string
  roofAreaSouthSqm?: number
  roofAreaSoutheastSqm?: number
  roofAreaSouthwestSqm?: number
  roofAreaEastSqm?: number
  roofAreaWestSqm?: number
  roofAreaNorthSqm?: number
  roofAreaNortheastSqm?: number
  roofAreaNorthwestSqm?: number
  roofAreaFlatSqm?: number
  existingPanelsKwp?: string
  calculationVersion: string
  versionUsed?: string
  visualisationPublicS3Url: string
  createdAt: string
  updatedAt: string
}

export interface EnergyBreakdown {
  id: number
  activityName: string
  category: string
  electricityKwh: string
  gasKwh: string
  isIndividualUsage: boolean
  electricityPerUse: string
  gasPerUse: string
  usesPerPersonPerYear: number
}

export interface EnergyCalculation {
  id: number
  home: number
  numOccupiers: number
  totalElectricityKwh: string
  totalGasKwh: string
  totalGasElectrified: string
  copUsed: string
  estimatedAnnualElectricityCost: string | null
  estimatedAnnualGasCost: string | null
  calculationSuccessful: boolean
  errorMessage: string | null
  calculationVersion: string
  createdAt: string
  updatedAt: string
  breakdowns: EnergyBreakdown[]
}

export interface HomeDetailResponse {
  home: Home
  heatLossCalculations: HeatLossCalculation[]
  solarAnalyses: SolarAnalysis[]
  energyCalculations: EnergyCalculation[]
  energyEstimates?: EnergyEstimate[]
  opportunityKanbanStage?: string | null
}

export interface HeatLossData {
  homeId: number
  totalHeatLoss: number
  wallHeatLoss: number
  roofHeatLoss: number
  floorHeatLoss: number
  windowHeatLoss: number
  doorHeatLoss: number
  ventilationHeatLoss: number
  recommendations: string[]
  lastUpdated: string
}

export const getHomeDetail = async (homeId: number): Promise<HomeDetailResponse> => {
  const response = await http.get(`/api/portfolio/homes/${homeId}/`)
  return response.data
}

export const getHeatLoss = async (homeId: number): Promise<HeatLossData> => {
  const response = await http.get(`/api/heat-loss/homes/${homeId}/heat-loss/`)
  return response.data
}

export const retriggerSolarAnalysis = async (homeId: number) => {
  return http.post(`/api/portfolio/homes/${homeId}/retrigger-solar/`)
}

export const resetEnergyCalculation = async (homeId: number) => {
  return http.post(`/api/portfolio/homes/${homeId}/reset-energy/`)
}

export interface DashboardStats {
  totalHomesSuccessful: number
  totalHomesVeryHighZeroBillPotential: number
  totalHomesHighZeroBillPotential: number
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await http.get("/api/portfolio/dashboard/")
  return response.data
}

export interface PortfolioStats {
  totalHomes: number
  unanalysedHomes: number
  zeroBillPotentialCounts: {
    veryHigh: number
    high: number
    medium: number
    low: number
  }
  roiPotentialCounts: {
    high: number
    medium: number
    low: number
  }
  avgRoi: number | null
  avgPayback: number | null
  avgUpgradeCost: number | null
  highestRoi: number | null
  totalDeploymentCost: number
  totalAnnualSavings: number
  availableBuiltForms: string[]
  /**
   * Homes per EPC band, A-G plus a "none" entry for unrated homes. A list rather than a
   * band-keyed object because the response interceptor camelises keys, which would
   * lowercase "A" into "a".
   */
  epcBandCounts: { band: string; count: number }[]
  totalSolarKwh: number
  totalSolarExportedKwh: number
  totalCo2SavingsKg: number
  totalEmbodiedKgCo2e: number
  carbonPaybackYears: number | null
}

export const getPortfolioStats = async (portfolioId?: number): Promise<PortfolioStats> => {
  const params = portfolioId ? { portfolio_id: portfolioId } : {}
  const response = await http.get("/api/portfolio/stats/", { params })
  return response.data
}

export interface HomeFinancials {
  currentGasCost: number
  currentElectricityCost: number
  currentEnergyCost: number
  gasCostAfterUpgrades: number
  electricityCostAfterUpgrades: number
  totalCostAfterUpgrades: number
  annualSavings: number
  solarPvCost: number
  batteryCost: number
  batteryKw: number
  heatPumpCost: number
  heatPumpGrant: number
  heatPumpKw: number | string
  gasBoilerDecommissioningCost: number
  totalAnnualSavings: number | null
  roi: number | null
  paybackPeriod: number | null
  solarPanels: number
  solarKw: number
  monthly: Array<{
    month: number
    currentDemandTotalCost: number
    generationKwh: number
    demandAfterUpgradesKwh: number
    finalCostAfterSavings: number
    exportKwh: number
  }>
}

export const getFinancials = async (homeId: number): Promise<HomeFinancials> => {
  const response = await http.get(`/api/energy/homes/${homeId}/financials/`)
  return response.data
}

export interface MonthlyEnergy {
  month: number
  energyKwh: number
}

export interface OrientationData {
  orientation: string
  azimuthDegrees: number
  peakPowerKwp: number
  monthlyEnergyKwh: MonthlyEnergy[]
  yearlyEnergyKwh: number
}

export interface PvGisCalculationResponse {
  homeId: number
  lat: number
  lng: number
  pitchDegrees: number
  lossPercent: number
  totalPeakPowerKwp: number
  totalYearlyEnergyKwh: number
  totalMonthlyEnergyKwh: MonthlyEnergy[]
  orientations: OrientationData[]
}

export const getSolarBreakdown = async (homeId: number, breakdownType: "recommended" | "maximum"): Promise<PvGisCalculationResponse> => {
  const response = await http.post(`/api/solar/breakdown/`, { homeId, breakdownType })
  return response.data
}

export interface HeatDemandBreakdownResponse {
  [key: string]: any
}

export const getHeatDemandBreakdown = async (homeId: number): Promise<HeatDemandBreakdownResponse> => {
  const response = await http.get(`/api/heat-loss/homes/${homeId}/heat-demand-breakdown/`)
  return response.data
}


export interface TenantPowerResponse {
  homeId: number
  eligible: boolean
  tenant: {
    discountRate: number
    electricityConsumptionKwh: number
    annualSaving: number
    monthlySaving: number
  }
  investor: {
    solarPanels: number
    annualGenerationKwh: number
    kwhPerPanel: number
    benchmarkKwhPerPanel: number
    generationRatio: number
    solarAnnualPayment: number
    batteryKw: number
    batteryAnnualPayment: number
    totalAnnualPayment: number
    annualReturn: number
    eligibleAssetCost: number | null
    netUpgradeCost: number | null
    roi: number | null
    paybackPeriod: number | null
    ashpExcluded: boolean
  }
  caveats: string[]
}

export const getTenantPower = async (homeId: number): Promise<TenantPowerResponse> => {
  const response = await http.get(`/api/energy/homes/${homeId}/tenant-power/`)
  return response.data
}

export interface BulkImportAddress {
  full_address: string
  postcode: string
}

export interface BulkImportResponse {
  message: string
  queuedCount: number
  portfolioId: number
  batchImportId: number
}

export interface BatchImportStatus {
  id: number
  status: "processing" | "completed" | "cancelled"
  totalAddresses: number
  counts: {
    pending: number
    processing: number
    analysed: number
    failed: number
    missingData: number
    cancelled: number
  }
  createdAt: string
}

export interface BatchImportAddress {
  id: number
  fullAddress: string
  status: "pending" | "processing" | "analysed" | "failed" | "missing_data" | "cancelled"
  errorMessage: string
  homeId: number | null
}

export interface BatchImportAddressListResponse {
  results: BatchImportAddress[]
  count: number
  nextPage: number | null
}

export const bulkImportHomes = async ({
  addresses,
  portfolioId,
}: {
  addresses: BulkImportAddress[]
  portfolioId?: number
}): Promise<BulkImportResponse> => {
  const response = await http.post("/api/portfolio/batch-add-homes/", { addresses, portfolio_id: portfolioId })
  return response.data
}

export const getBatchImportStatus = async (batchImportId: number): Promise<BatchImportStatus> => {
  const response = await http.get(`/api/portfolio/batch-imports/${batchImportId}/`)
  return response.data
}

export const getLatestProcessingBatchImport = async (): Promise<BatchImportStatus> => {
  const response = await http.get("/api/portfolio/batch-imports/latest-processing/")
  return response.data
}

export const getBatchImportAddresses = async (
  batchImportId: number,
  page: number = 1,
  pageSize: number = 50
): Promise<BatchImportAddressListResponse> => {
  const response = await http.get(`/api/portfolio/batch-imports/${batchImportId}/addresses/`, {
    params: { page, page_size: pageSize },
  })
  return response.data
}

export const cancelBatchImport = async (batchImportId: number): Promise<BatchImportStatus> => {
  const response = await http.post(`/api/portfolio/batch-imports/${batchImportId}/cancel/`)
  return response.data
}

export const getPortfolioCriteria = (portfolioId: number) =>
  http.get(`/api/portfolio/portfolios/${portfolioId}/criteria/`)

export const savePortfolioCriteria = (portfolioId: number, data: any) =>
  http.put(`/api/portfolio/portfolios/${portfolioId}/criteria/`, data)

export const onboardingSetup = (data: { name: string; [key: string]: any }) =>
  http.post("/api/portfolio/onboarding/", data)

export interface OperationalCarbonAnalysis {
  homeId: number
  emissionFactorYear: number
  gasCo2KgPre: number
  electricityCo2KgPre: number
  totalCo2KgPre: number
  gasCo2KgPost: number
  electricityCo2KgPost: number
  totalCo2KgPost: number
  co2SavingsKg: number
  co2ReductionPct: number
}

export const getHomeOperationalCarbon = async (homeId: number): Promise<OperationalCarbonAnalysis> => {
  const response = await http.get(`/api/energy/homes/${homeId}/carbon/operational/`)
  return response.data
}

export interface EmbodiedCarbonItem {
  technology: string
  totalEmbodiedCarbonKgco2e: number
  annualisedEmbodiedCarbonKgco2e: number
  expectedLifeYears: number
  confidence: string
}

export interface HomeEmbodiedCarbon {
  homeId: number
  status: string
  totalEmbodiedCarbonKgco2e: number | null
  annualisedEmbodiedCarbonKgco2e: number | null
  annualOperationalCarbonSavingKgco2e: number | null
  carbonPaybackYears: number | null
  netCarbonBenefitKgco2e: number | null
  items: EmbodiedCarbonItem[]
}

export const getHomeEmbodiedCarbon = async (homeId: number): Promise<HomeEmbodiedCarbon> => {
  const response = await http.get(`/api/energy/homes/${homeId}/carbon/embodied/`)
  return response.data
}
